/**
 * Privacy Settings Hooks
 * 
 * TanStack Query hooks for privacy settings operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { privacyService } from '../services/privacy-service';
import { getCurrentUserId } from '../lib/supabase';
import type {
  UpdatePrivacySettingsInput,
} from '../types/security.types';

/**
 * Query keys for privacy settings queries
 */
export const privacyKeys = {
  all: ['privacy'] as const,
  current: () => [...privacyKeys.all, 'current'] as const,
};

/**
 * Hook to get current user's privacy settings
 */
export function usePrivacySettings() {
  return useQuery({
    queryKey: privacyKeys.current(),
    queryFn: async () => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      const result = await privacyService.getPrivacySettings(userId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to update privacy settings
 */
export function useUpdatePrivacySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdatePrivacySettingsInput) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      const result = await privacyService.updatePrivacySettings(userId, input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    onSuccess: async () => {
      // Invalidate privacy settings query to refetch updated data
      await queryClient.invalidateQueries({ queryKey: privacyKeys.current() });
    },
  });
}

